/**
 * AdminContestArchivePage — Cutest Pluck contest archive.
 *
 * Route: `/admin/contest/archive`. Added 2026-07-21 as part of the
 * Contest C+D+E build dispatch (Feature C — multi-round support).
 *
 * Renders a read-only list of every round (newest first) with
 * status pill, name/round_number, key dates, and inline
 * `entries_current` count. Closed rounds have `frozen_at` displayed
 * so the admin can trace when finalization happened.
 *
 * Design discipline (Family L):
 *   - No inline hex; tokens only (`text-pp-orange`, `bg-pp-surface-2`).
 *   - Section heading uses phrase-accent orange per Guard 11
 *     refinement — not the full line.
 *   - Small, dense-table layout matches AdminContestEntriesPage's
 *     visual rhythm.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Archive as ArchiveIcon } from "lucide-react";
import AdminContestTabs from "@/components/admin/AdminContestTabs";

function StatusPill({ status }) {
  const style = {
    active: "bg-pp-surface-2 text-brand-orange",
    closed: "bg-pp-surface-2 text-pp-text",
    draft: "bg-pp-surface-2 text-muted-foreground",
  }[status] || "bg-pp-surface-2 text-pp-text";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${style}`}
      data-testid={`admin-contest-archive-status-${status}`}
    >
      {status}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function AdminContestArchivePage() {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contest/rounds");
      setRounds(r.data?.rounds || []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load contest archive.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-contest-archive-page">
      <AdminContestTabs />
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-pp-text">
            <span className="text-brand-orange">Archive</span> — all contest rounds
          </h1>
          <p className="text-sm text-muted-foreground">
            Read-only history of every round.
          </p>
        </div>
      </div>

      {loading && (
        <div
          className="flex items-center justify-center rounded-2xl border border-border bg-pp-surface p-10"
          data-testid="admin-contest-archive-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin text-brand-orange" />
        </div>
      )}

      {!loading && rounds.length === 0 && (
        <div
          className="rounded-2xl border border-border bg-pp-surface p-10 text-center"
          data-testid="admin-contest-archive-empty"
        >
          <ArchiveIcon
            className="mx-auto h-8 w-8 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="mt-3 text-sm font-semibold text-pp-text">
            No rounds yet.
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Rounds will appear here once the first one is created or migrated.
          </p>
        </div>
      )}

      {!loading && rounds.length > 0 && (
        <div className="space-y-3" data-testid="admin-contest-archive-list">
          {rounds.map((r) => (
            <div
              key={r.id}
              className="flex flex-col gap-3 rounded-2xl border border-border bg-pp-surface p-5 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`admin-contest-archive-row-${r.id}`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Human override 2026-07-21 Step 2: `name` is STANDALONE
                      (no baked-in "Round N"). Render round_number
                      structurally from its own field so a future
                      round named `Cutest Pluck on the Block — 2027`
                      auto-composes to `… — 2027 (Round 2)` cleanly. */}
                  <h2
                    className="font-heading text-lg font-bold text-pp-text"
                    data-testid={`admin-contest-archive-name-${r.id}`}
                  >
                    {r.name || `Round ${r.round_number}`}
                  </h2>
                  <span
                    className="text-sm text-muted-foreground"
                    data-testid={`admin-contest-archive-round-number-${r.id}`}
                  >
                    (Round {r.round_number})
                  </span>
                  <StatusPill status={r.status} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Entry {formatDate(r.entry_open_at)} → {formatDate(r.entry_close_at)}
                  {" · "}
                  Voting closes {formatDate(r.voting_close_at)}
                  {r.frozen_at && (
                    <>
                      {" · "}
                      <span data-testid={`admin-contest-archive-frozen-${r.id}`}>
                        Frozen {formatDate(r.frozen_at)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 text-right">
                <span
                  className="text-sm font-semibold text-pp-text"
                  data-testid={`admin-contest-archive-entries-${r.id}`}
                >
                  {r.entries_current ?? 0} entries
                </span>
                <span className="text-xs text-muted-foreground">
                  {r.winner_count ?? 10} winner{r.winner_count === 1 ? "" : "s"}
                  {" · "}
                  {r.max_entries == null ? "unlimited cap" : `cap ${r.max_entries}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
